/**
 * Client-side signature policy — mirrors the backend `_shared/formSignatures.ts`
 * contract (server remains authoritative). Pure functions only; safe to
 * exercise in Playwright VM-style specs without a running app.
 */

/** Signing capacities. Fixed enum — never arbitrary text. */
export const SIGNER_ROLES = ['self', 'parentGuardian'];

/** Consent template version. Client sends this; server rebuilds authoritative wording. */
export const CONSENT_VERSION = 'reach-esign-v1';

/** Bounds mirrored from the submit-form contract (plan §6.3). */
export const SIGNER_NAME_MAX = 200;
export const AGREEMENT_TEXT_MAX = 5000;

export const SIGNER_ROLE_LABELS = {
    self: 'Myself',
    parentGuardian: 'Parent/Legal Guardian',
};

/** Creator-side wording for the "Who can sign" checkboxes. */
export const SIGNER_ROLE_BUILDER_LABELS = {
    self: 'For myself',
    parentGuardian: 'Parent/Legal Guardian',
};

/** Compact capacity label used in entry summaries. */
export const SIGNER_ROLE_SUMMARY = {
    self: 'self',
    parentGuardian: 'parent/legal guardian',
};

const GUARDIAN_ROLES = new Set(['parentGuardian']);

/** True when the capacity signs on behalf of a named participant. */
export const roleNeedsParticipant = (role) => GUARDIAN_ROLES.has(role);

/**
 * Baseline electronic-signature consent copy per capacity.
 * CANNOT be disabled or replaced by creators. Legal review pending — treat
 * as draft wording (see plan §4.2).
 */
export const consentTextForRole = (role) => {
    if (GUARDIAN_ROLES.has(role)) {
        return "I confirm this is my signature, I am the participant's parent or legal guardian, and I am authorized to sign for them. I intend to sign this form electronically and agree that my electronic signature has the same effect as my handwritten signature.";
    }
    return 'I confirm this is my signature and I intend to sign this form electronically. I agree that my electronic signature has the same effect as my handwritten signature.';
};

/** Roles the creator has enabled for this field (defaults to all). */
export const allowedSignerRoles = (field) => {
    const configured = field?.config?.allowedSignerRoles;
    const list = Array.isArray(configured) && configured.length ? configured : SIGNER_ROLES;
    return list.filter((role) => SIGNER_ROLES.includes(role));
};

/**
 * True when a draft signature value carries no content at all — the state an
 * untouched field should serialize as `null` rather than a partial object.
 */
export const isSignatureDraftEmpty = (value) => {
    if (!value || typeof value !== 'object' || value.kind !== 'signature') return true;
    return !value.image?.base64
        && !value.signerName?.trim()
        && !value.signerRole
        && !value.participantName?.trim()
        && value.consentAccepted !== true;
};

/**
 * Validate a signature field value (draft or normalized). Returns '' when
 * valid, otherwise the first user-correctable error message. Mirrors the
 * server rules: complete-or-empty, allowed capacity, trimmed name, participant
 * for guardian roles, explicit consent, matching consent version.
 */
export const validateSignatureDraft = (field, value) => {
    if (value === null || value === undefined || isSignatureDraftEmpty(value)) {
        return field?.required ? `${field?.label || 'Signature'} is required.` : '';
    }
    if (typeof value !== 'object' || value.kind !== 'signature') {
        return 'Invalid signature.';
    }
    const allowed = allowedSignerRoles(field);
    if (!value.signerRole || !SIGNER_ROLES.includes(value.signerRole)) {
        return 'Choose how you are signing.';
    }
    if (!allowed.includes(value.signerRole)) {
        return 'This signing option is not allowed for this field.';
    }
    const name = typeof value.signerName === 'string' ? value.signerName.trim() : '';
    if (!name) return 'Enter the signer\u2019s full legal name.';
    if (name.length > SIGNER_NAME_MAX) return 'Signer name is too long.';
    if (roleNeedsParticipant(value.signerRole)) {
        const participant = typeof value.participantName === 'string' ? value.participantName.trim() : '';
        if (!participant) return 'Enter the participant name you are signing for.';
    }
    if (!value.image?.base64 && !value.image?.path) {
        return 'Draw your signature in the box.';
    }
    if (value.consentAccepted !== true) {
        return 'Confirm the signature consent.';
    }
    if (value.consentVersion !== (field?.config?.consentVersion || CONSENT_VERSION)) {
        return 'This consent is out of date. Please sign again.';
    }
    return '';
};

/**
 * Validate a `profile` field value. Wire shape is exactly null or { personID }.
 */
export const validateProfileValue = (field, value) => {
    if (value === null || value === undefined) {
        return field?.required ? `${field?.label || 'Profile'} is required.` : '';
    }
    if (typeof value !== 'object' || Array.isArray(value) || typeof value.personID !== 'string' || !value.personID) {
        return 'Select a single profile.';
    }
    return '';
};

/**
 * Normalize the partial local draft into the wire shape (or null when nothing
 * has been entered). Caller merges this into the form payload.
 */
export const toSignatureDraft = ({ signerName, signerRole, participantName, consentAccepted, image }) => {
    const draft = {
        kind: 'signature',
        version: 1,
        signerName: signerName || null,
        signerRole: signerRole || null,
        participantName: roleNeedsParticipant(signerRole) ? (participantName || null) : null,
        consentAccepted: consentAccepted === true,
        consentVersion: CONSENT_VERSION,
        image: image ? { base64: image.base64, mimeType: 'image/png' } : null,
    };
    return isSignatureDraftEmpty(draft) ? null : draft;
};

/** Compact one-line summary for entries tables. */
export const signatureSummary = (value) => {
    if (!value || typeof value !== 'object') return null;
    const name = value.signerName?.trim();
    if (!name) return null;
    const role = SIGNER_ROLE_SUMMARY[value.signerRole] || value.signerRole;
    const when = value.signedAt ? ` · ${new Date(value.signedAt).toLocaleDateString()}` : '';
    return `Signed by ${name}${role ? ` (${role})` : ''}${when}`;
};
